import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Separator } from '../../components/ui/separator';
import { Label } from '../../components/ui/label';
import {
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  AlignVerticalJustifyStart, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { useDiagramStore } from '../../stores/diagramStore';
import { useTranslation } from '../../hooks/useTranslation';

type HorizontalAlign = 'left' | 'center' | 'right' | 'justify';
type VerticalAlign = 'top' | 'middle' | 'bottom';

interface AlignmentState {
  horizontal: HorizontalAlign;
  vertical: VerticalAlign;
}

const TextAlignmentToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const pendingAlignmentRef = React.useRef<AlignmentState | null>(null);
  const selectionKey = React.useMemo(
    () => [...context.selection].sort().join(','),
    [context.selection],
  );
  const lastSelectionKeyRef = React.useRef(selectionKey);

  const readAlignmentFromContext = (): AlignmentState => ({
    horizontal: (context.selectionStyles?.textAlign as HorizontalAlign) || 'center',
    vertical: (context.selectionStyles?.verticalAlign as VerticalAlign) || 'middle',
  });

  const [alignment, setAlignment] = React.useState<AlignmentState>(readAlignmentFromContext);

  React.useEffect(() => {
    if (lastSelectionKeyRef.current !== selectionKey) {
      lastSelectionKeyRef.current = selectionKey;
      pendingAlignmentRef.current = null;
    }

    const fromContext = readAlignmentFromContext();

    if (pendingAlignmentRef.current !== null) {
      const p = pendingAlignmentRef.current;
      if (fromContext.horizontal === p.horizontal && fromContext.vertical === p.vertical) {
        pendingAlignmentRef.current = null;
        setAlignment(fromContext);
      }
    } else {
      setAlignment(fromContext);
    }
  }, [
    context.selectionStyles?.textAlign,
    context.selectionStyles?.verticalAlign,
    selectionKey,
  ]);
  
  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;

  const handleAlignmentChange = (horizontal?: HorizontalAlign, vertical?: VerticalAlign) => {
    const newAlignment: AlignmentState = {
      horizontal: horizontal ?? alignment.horizontal,
      vertical: vertical ?? alignment.vertical,
    };
    pendingAlignmentRef.current = newAlignment;
    setAlignment(newAlignment);
    onExecute(newAlignment);
  };

  const handlePositionClick = (row: number, col: number) => {
    const verticalMap: VerticalAlign[] = ['top', 'middle', 'bottom'];
    const horizontalMap: HorizontalAlign[] = ['left', 'center', 'right'];
    
    handleAlignmentChange(horizontalMap[col], verticalMap[row]);
  };

  const isPositionActive = (row: number, col: number): boolean => {
    const verticalMap: VerticalAlign[] = ['top', 'middle', 'bottom'];
    const horizontalMap: HorizontalAlign[] = ['left', 'center', 'right'];
    
    return alignment.vertical === verticalMap[row] && 
           alignment.horizontal === horizontalMap[col];
  };

  const getAlignmentIcon = () => {
    switch (alignment.horizontal) {
      case 'left': return AlignLeft;
      case 'center': return AlignCenter;
      case 'right': return AlignRight;
      case 'justify': return AlignJustify;
      default: return AlignCenter;
    }
  };

  const AlignIcon = getAlignmentIcon();

  // Detect if selection contains only text nodes
  const isTextOnly = (() => {
    try {
      const ids = Array.from(context.selection);
      const nodes = useDiagramStore.getState().nodes as Array<{ id?: string; type?: string }>;
      if (ids.length === 0) return false;
      return ids.every(id => nodes.some(n => n.id === id && n.type === 'text'));
    } catch { return false; }
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isDisabled}
          data-testid="tid.tools.textAlignment.trigger"
          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('properties.textAlignment', { defaultValue: 'Text alignment' })}
        >
          <AlignIcon className="h-5 w-5 text-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-3" align="start">
        {/* Label Header */}
        <div className="mb-3 pb-2 border-b border-border">
          <h3 className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{t('properties.textAlignment', { defaultValue: 'Text Alignment' })}</h3>
        </div>

        <div className="flex gap-3">
          {/* Left Column - Position Grid */}
          {!isTextOnly && (
            <>
              <div className="flex-shrink-0">
                <div className="grid grid-cols-3 gap-0.5 w-[90px]">
                  {[0, 1, 2].map((row) => (
                    <React.Fragment key={row}>
                      {[0, 1, 2].map((col) => (
                        <button
                          key={`${row}-${col}`}
                          className={cn(
                            "w-7 h-7 border rounded transition-all",
                            "hover:bg-muted",
                            isPositionActive(row, col)
                              ? "bg-primary border-primary"
                              : "bg-background border-border"
                          )}
                          data-testid={`tid.tools.textAlignment.position.${row}.${col}`}
                          onClick={() => handlePositionClick(row, col)}
                        >
                          <div className="w-full h-full flex items-center justify-center">
                            <div
                              className={cn(
                                "w-1.5 h-1.5 rounded-full",
                                isPositionActive(row, col)
                                  ? "bg-primary-foreground"
                                  : "bg-muted-foreground"
                              )}
                            />
                          </div>
                        </button>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Vertical Separator */}
              <Separator orientation="vertical" className="h-auto" />
            </>
          )}

          {/* Right Column - Alignment Options */}
          <div className="space-y-3 flex-1">
            {/* Horizontal Alignment */}
            <div className="space-y-1.5">
              <Label className="text-[10px] font-medium text-muted-foreground">{t('properties.horizontal', { defaultValue: 'Horizontal' })}</Label>
              <div className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  className={cn(
                    "h-7 px-2 rounded flex items-center justify-center transition-all",
                    alignment.horizontal === 'left'
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  data-testid="tid.tools.textAlignment.horizontal.left"
                  onClick={() => handleAlignmentChange('left')}
                  aria-label={t('properties.alignLeft', { defaultValue: 'Align left' })}
                >
                  <AlignLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className={cn(
                    "h-7 px-2 rounded flex items-center justify-center transition-all",
                    alignment.horizontal === 'center'
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  data-testid="tid.tools.textAlignment.horizontal.center"
                  onClick={() => handleAlignmentChange('center')}
                  aria-label={t('properties.alignCenter', { defaultValue: 'Align center' })}
                >
                  <AlignCenter className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className={cn(
                    "h-7 px-2 rounded flex items-center justify-center transition-all",
                    alignment.horizontal === 'right'
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  data-testid="tid.tools.textAlignment.horizontal.right"
                  onClick={() => handleAlignmentChange('right')}
                  aria-label={t('properties.alignRight', { defaultValue: 'Align right' })}
                >
                  <AlignRight className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className={cn(
                    "h-7 px-2 rounded flex items-center justify-center transition-all",
                    alignment.horizontal === 'justify'
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  data-testid="tid.tools.textAlignment.horizontal.justify"
                  onClick={() => handleAlignmentChange('justify')}
                  aria-label={t('properties.alignJustify', { defaultValue: 'Justify' })}
                >
                  <AlignJustify className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Vertical Alignment */}
            {!isTextOnly && (
              <div className="space-y-1.5">
                <Label className="text-[10px] font-medium text-muted-foreground">{t('properties.vertical', { defaultValue: 'Vertical' })}</Label>
                <div className="grid grid-cols-3 gap-1">
                  <button
                    type="button"
                    className={cn(
                      "h-7 px-2 rounded flex items-center justify-center transition-all",
                      alignment.vertical === 'top'
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                    data-testid="tid.tools.textAlignment.vertical.top"
                    onClick={() => handleAlignmentChange(undefined, 'top')}
                    aria-label={t('properties.alignTop', { defaultValue: 'Align top' })}
                  >
                    <AlignVerticalJustifyStart className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "h-7 px-2 rounded flex items-center justify-center transition-all",
                      alignment.vertical === 'middle'
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                    data-testid="tid.tools.textAlignment.vertical.middle"
                    onClick={() => handleAlignmentChange(undefined, 'middle')}
                    aria-label={t('properties.alignMiddle', { defaultValue: 'Align middle' })}
                  >
                    <AlignVerticalJustifyCenter className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "h-7 px-2 rounded flex items-center justify-center transition-all",
                      alignment.vertical === 'bottom'
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                    data-testid="tid.tools.textAlignment.vertical.bottom"
                    onClick={() => handleAlignmentChange(undefined, 'bottom')}
                    aria-label={t('properties.alignBottom', { defaultValue: 'Align bottom' })}
                  >
                    <AlignVerticalJustifyEnd className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class TextAlignmentTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'text-alignment',
    name: 'tools.textAlignment',
    icon: 'align-center',
    category: 'style',
    description: 'Change text alignment and position',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.TEXT_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0;
  }

  execute(params?: AlignmentState): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    const stylePatch: Record<string, HorizontalAlign | VerticalAlign> = {};
    
    if (params.horizontal) {
      stylePatch.textAlign = params.horizontal;
    }
    
    if (params.vertical) {
      stylePatch.verticalAlign = params.vertical;
    }

    return Object.keys(stylePatch).length > 0 ? new ChangeNodeStyleCommand(selectedIds, 'stylePatch', stylePatch) : null;
  }

  getToolbarComponent() {
    return TextAlignmentToolComponent;
  }

  getFloatingPanelComponent() {
    return TextAlignmentToolComponent;
  }
}
